import { ComponentType, lazy, Suspense, useState } from 'react';

import { Loading } from '@frontend/views';

const expectedErrors = [
  'Failed to fetch dynamically imported module', // Chrome
  'error loading dynamically imported module', // Firefox
];

export const loadComponent = <TModule,>(
  loadFn: () => Promise<TModule>,
  resolveComponent: (module: TModule) => ComponentType<any>,
) => {
  let loaded: ComponentType<any> | undefined;
  let pending: Promise<{ default: ComponentType<any> }> | undefined;

  const load = () => {
    pending ??= loadFn()
      .then((module) => {
        loaded = resolveComponent(module);
        return { default: loaded };
      })
      .catch((e) => {
        // Don't cache a failure, the next render gets a fresh attempt.
        pending = undefined;
        throw e;
      });

    return pending;
  };

  const LazyLoader = lazy(() => {
    return load().catch((e) => {
      console.warn('Error loading component:', e);

      if (
        e instanceof TypeError &&
        expectedErrors.find((expectedError) =>
          e.message.includes(expectedError),
        )
      ) {
        /*
         * This does not take user being offline into account. This is tested however
         * using browser throttling set to offline. Using that we get the correct behavior
         * of reloading -> app initializes -> app tries to preload routes -> fails.
         *
         * If this would fail we could end up in an infinite loop of reloading.
         */
        console.warn('Reloading page due to error loading component');
        window.location.reload();

        // reload() is happening instantly but we need to return a valid component
        // to avoid other errors like e.g compile errors here.
        return { default: () => <div /> };
      }

      throw e;
    });
  });

  const LoadedOrLazy = () => {
    // A lazy component suspends on its first render even when its module is
    // already loaded, which flashes the full-screen fallback. Rendering the
    // resolved component directly avoids that. The choice is made once per
    // mount, switching branches later would remount the page.
    const [Loaded] = useState(() => loaded);

    if (Loaded) {
      return <Loaded />;
    }

    return (
      <Suspense fallback={<Loading />}>
        <LazyLoader />
      </Suspense>
    );
  };

  /**
   * Starts loading the module ahead of the first render. Failures are
   * swallowed (the component retries when it is actually rendered).
   */
  const preload = async () => {
    try {
      await load();
    } catch {
      // Retried on render, which also owns the reload-on-stale-chunk handling.
    }
  };

  return Object.assign(LoadedOrLazy, { preload });
};
